import { useState, useEffect, useCallback } from 'react';
import DesignSpecPage from './DesignSpecPage';

const PROMO_SLIDES = [
  '/slab-edit-1.webp',
  '/slab-edit-2.webp',
  '/slab-edit-3.webp',
];

const MobilePromoCard = () => {
  const [idx, setIdx] = useState(0);

  const advance = useCallback(() => {
    setIdx(i => (i + 1) % PROMO_SLIDES.length);
  }, []);

  useEffect(() => {
    const t = setInterval(advance, 4000);
    return () => clearInterval(t);
  }, [advance]);

  return (
    <a
      href="https://www.elegantfittings.co.ke/promotions"
      target="_blank"
      rel="noopener noreferrer"
      className="lo-mobile-promo"
    >
      <img
        key={idx}
        src={PROMO_SLIDES[idx]}
        alt="The Slab Edit — Up to 40% OFF"
        className="lo-mobile-promo-img"
      />
      <div className="lo-mobile-promo-overlay">
        <span className="lo-mobile-promo-title">The Slab Edit</span>
        <span className="lo-mobile-promo-offer">Up to 40% OFF · Explore →</span>
      </div>
      <div className="lo-mobile-promo-dots">
        {PROMO_SLIDES.map((_, i) => (
          <span key={i} className={`lo-mobile-promo-dot${i === idx ? ' active' : ''}`} />
        ))}
      </div>
    </a>
  );
};

const ALL_APPS = [
  {
    app: 'Kitchens',
    label: 'Kitchen',
    layouts: [
      { num: 1, thumb: '/mainpng2.png',        label: 'Kitchen + Cabinet', sub: 'With cabinet colour options', prev1: '/mainpng2-Gprev1.webp',          prev2: '/mainpng2-CRprev2.webp' },
      { num: 2, thumb: '/mainpng.png',          label: 'Island Layout',     sub: 'Open-plan island kitchen',   prev1: '/mainpngprev1.webp',              prev2: '/mainpngprev2.webp' },
      { num: 3, thumb: '/mainpng3.png',         label: 'L-Shape Kitchen',   sub: 'Corner kitchen design',      prev1: '/mainpng3prev1.webp',             prev2: '/mainpng3prev2.webp' },
    ],
  },
  {
    app: 'Vanities',
    label: 'Vanities',
    layouts: [
      { num: 1, thumb: '/mainvanitiespng.png',  label: 'Double Vanity',  sub: 'His & hers basin setup',    prev1: '/mainvanitiespngprev1.webp',  prev2: '/mainvanitiespngprev2.webp' },
      { num: 2, thumb: '/mainvanitiespng1.png', label: 'Floating Sink',  sub: 'Wall-mounted modern sink',  prev1: '/mainvanitiespng1prev1.webp', prev2: '/mainvanitiespng1prev2.webp' },
      { num: 3, thumb: '/mainvanitiespng2.png', label: 'Single Basin',   sub: 'Minimalist single vanity',  prev1: '/mainvanitiespng2prev1.webp', prev2: '/mainvanitiespng2prev2.webp' },
    ],
  },
  {
    app: 'Wall-cladding',
    label: 'Wall Cladding',
    layouts: [
      { num: 1, thumb: '/mainwallcladding.png',  label: 'Feature Wall 1', sub: 'Living room accent wall',  prev1: '/mainwallcladdingprev1.webp',   prev2: '/mainwallcladdingprev2.webp' },
      { num: 2, thumb: '/mainwallcladding1.png', label: 'Feature Wall 2', sub: 'Media wall with shelving', prev1: '/mainwallcladding1prev1.webp',  prev2: '/mainwallcladding1prev2.webp' },
      { num: 3, thumb: '/mainwallcladding2.png', label: 'Feature Wall 3', sub: 'Warm-toned cladding',      prev1: '/mainwallcladding2prev1.webp',  prev2: '/mainwallcladding2prev2.webp' },
      { num: 4, thumb: '/mainwallcladding3.png', label: 'Feature Wall 4', sub: 'Full-height stone panel',  prev1: '/mainwallcladding3prev1.webp',  prev2: '/mainwallcladding3prev2.webp' },
    ],
  },
];

const ALL_SLIDES = ALL_APPS.flatMap(({ label: appLabel, layouts }) =>
  layouts.map(l => ({ src: l.prev1, label: l.label, appLabel }))
);

const AnimatedCard = ({ layout, onSelect, app }) => {
  const [hovered, setHovered] = useState(false);
  const src = hovered ? layout.prev2 : layout.prev1;

  return (
    <button
      className="layout-overview-card"
      onClick={() => onSelect(app, layout.num)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="layout-overview-img-wrap">
        <img
          key={src}
          src={src}
          alt={layout.label}
          loading="lazy"
          className="layout-overview-img"
        />
      </div>
      <div className="layout-overview-card-body">
        <span className="layout-overview-card-label">{layout.label}</span>
        <span className="layout-overview-card-sub">{layout.sub}</span>
      </div>
    </button>
  );
};

const FEATURES = [
  { icon: '⬡', text: '120+ surfaces' },
  { icon: '⇄', text: 'Compare side by side' },
  { icon: '◎', text: 'Request a sample' },
];

const HeroSlider = () => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % ALL_SLIDES.length), 3200);
    return () => clearInterval(t);
  }, []);

  const slide = ALL_SLIDES[idx];

  return (
    <div className="lo-hero-sticky">
      <div className="lo-hero-card">
        <div className="lo-hero-img-wrap">
          <img key={slide.src} src={slide.src} alt={slide.label} className="lo-hero-img" />
        </div>
        <div className="lo-hero-text">
          <p className="lo-hero-app-label">{slide.appLabel}</p>
          <h2 className="lo-hero-title">See it in your space</h2>
          <p className="lo-hero-sub">
            Explore 120+ luxury stone surfaces across kitchens, vanities and walls — before you commit.
          </p>
          <div className="lo-hero-dots">
            {ALL_SLIDES.map((_, i) => (
              <button
                key={i}
                className={`lo-hero-dot${i === idx ? ' active' : ''}`}
                onClick={() => setIdx(i)}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="lo-hero-features">
        {FEATURES.map(({ icon, text }) => (
          <span key={text} className="lo-hero-feat">
            <span className="lo-hero-feat-icon">{icon}</span>
            {text}
          </span>
        ))}
      </div>
    </div>
  );
};

const THICKNESS_DATA = {
  Kitchens: {
    rows: [
      { mm: '12mm', suited: 'Kitchen worktops (lighter), vanity tops, splashbacks', brands: 'Caesarstone · Dekton · Atlas Plan' },
      { mm: '20mm', suited: 'Standard countertops, island tops, dining tables, reception desks', brands: 'All 5 brands — most common' },
      { mm: '30mm', suited: 'Statement islands, commercial counters, outdoor kitchens', brands: 'Caesarstone · Dekton · Scalea · Sensa' },
    ],
    tip: 'Residential worktop → 20mm · Statement or commercial → 30mm',
  },
  Vanities: {
    rows: [
      { mm: '12mm', suited: 'Lighter vanity tops, furniture panels, splashbacks', brands: 'Caesarstone · Dekton · Atlas Plan' },
      { mm: '20mm', suited: 'Standard vanity tops, basin surrounds', brands: 'All 5 brands — most common' },
    ],
    tip: 'Standard vanity → 20mm · Slim or wall-hung → 12mm',
  },
  'Wall-cladding': {
    rows: [
      { mm: '6mm',  suited: 'Decorative facades, furniture panels, lightweight cladding', brands: 'Atlas Plan only — requires substrate backing' },
      { mm: '8mm',  suited: 'Feature walls, splashbacks, lightweight cladding', brands: 'Dekton only — not for countertop use' },
      { mm: '12mm', suited: 'Feature walls, splashbacks, wall panels', brands: 'Caesarstone · Dekton · Atlas Plan' },
    ],
    tip: 'Wall or furniture panel → 6–12mm',
  },
};

const ThicknessGuide = ({ app }) => {
  const [open, setOpen] = useState(false);
  const data = THICKNESS_DATA[app];
  if (!data) return null;
  return (
    <div className={`lo-thickness-guide${open ? ' is-open' : ''}`}>
      <button className="lo-thickness-toggle" onClick={() => setOpen(o => !o)}>
        <span className="lo-thickness-heading">Thickness Guide</span>
        <span className="lo-thickness-chevron">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <>
          <div className="lo-thickness-rows">
            {data.rows.map(({ mm, suited, brands }) => (
              <div key={mm} className="lo-thickness-row">
                <span className="lo-thickness-mm">{mm}</span>
                <div className="lo-thickness-info">
                  <span className="lo-thickness-suited">{suited}</span>
                  <span className="lo-thickness-brands">{brands}</span>
                </div>
              </div>
            ))}
          </div>
          <p className="lo-thickness-tip">{data.tip}</p>
        </>
      )}
    </div>
  );
};

const LayoutOverview = ({ onSelect, showPrompt, filterApp }) => {
  const [showDesignSpec, setShowDesignSpec] = useState(false);

  const visibleApps = filterApp
    ? ALL_APPS.filter(a => a.app === filterApp)
    : ALL_APPS;

  const showHero = !showPrompt && !filterApp;

  return (
    <>
      {showDesignSpec && <DesignSpecPage onClose={() => setShowDesignSpec(false)} />}

      <div className={`layout-overview${showPrompt ? ' layout-overview--prompt' : ''}${filterApp ? ' layout-overview--filtered' : ''}`}>

        {showHero ? (
          <HeroSlider />
        ) : (
          <div className="layout-overview-banner">
            {showPrompt
              ? 'Pick a space first, then apply your chosen stone'
              : `Choose a ${visibleApps[0]?.label || filterApp} layout`}
          </div>
        )}

        {showHero && <MobilePromoCard />}
        {showHero && (
          <div className="lo-scroll-hint" aria-hidden="true">
            <span className="lo-scroll-chevron">&#8964;</span>
          </div>
        )}

        {visibleApps.map(({ app, label, layouts }) => (
          <div key={app} className="layout-overview-section">
            <h3 className="layout-overview-section-title">{label}</h3>
            <ThicknessGuide app={app} />
            <div className="layout-overview-grid">
              {layouts.map((layout) => (
                <AnimatedCard
                  key={layout.num}
                  layout={layout}
                  app={app}
                  onSelect={onSelect}
                />
              ))}
            </div>
          </div>
        ))}

        <button className="lo-dsp-card" onClick={() => setShowDesignSpec(true)}>
          <span className="lo-dsp-card-icon">◈</span>
          <div className="lo-dsp-card-text">
            <span className="lo-dsp-card-title">Design &amp; Specification</span>
            <span className="lo-dsp-card-sub">Surface finishes · thickness options · slab formats · edge profiles</span>
            <span className="lo-dsp-card-learn">Learn more →</span>
          </div>
        </button>

      </div>
    </>
  );
};

export default LayoutOverview;
